import { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";

export default function PublicPsyForm() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [setorFuncao, setSetorFuncao] = useState({ setor: "", funcao: "" });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0); // 0=intro, 1..N=dimensions, last=consent+submit
  const [consent, setConsent] = useState(false);
  const startedAt = useRef(Date.now());
  const consentRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    api.get(`/psy/public/${token}`)
      .then((r) => { if (mounted) { setMeta(r.data); startedAt.current = Date.now(); } })
      .catch((e) => { if (mounted) setError(e.response?.data?.detail || "Campanha não encontrada"); });
    return () => { mounted = false; };
  }, [token]);

  const groupedByDim = useMemo(() => {
    if (!meta) return [];
    const map = {};
    meta.test.questions.forEach((q) => {
      if (!map[q.dimensao_id]) map[q.dimensao_id] = [];
      map[q.dimensao_id].push(q);
    });
    return meta.test.dimensions.map((d) => ({ dim: d, questions: map[d.id] || [] }));
  }, [meta]);

  const totalQuestions = meta?.test?.questions?.length || 0;
  const answeredCount = Object.keys(answers).length;
  const progress = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <i className="fa-solid fa-triangle-exclamation text-4xl text-rose-500 mb-3"></i>
          <h2 className="font-bold text-slate-900 mb-2">Não foi possível abrir</h2>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!meta) return <div className="min-h-screen flex items-center justify-center text-slate-400">Carregando…</div>;

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-lg text-center" data-testid="psy-success">
          <i className="fa-solid fa-circle-check text-6xl text-emerald-500 mb-4"></i>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Obrigado pela sua contribuição!</h1>
          <p className="text-slate-600">Sua resposta foi registrada de forma anônima e irá compor o diagnóstico psicossocial da empresa.</p>
          <p className="text-xs text-slate-500 mt-4 italic">Nenhuma resposta individual é exibida no relatório final.</p>
        </div>
      </div>
    );
  }

  const setAnswer = (qid, val) => setAnswers({ ...answers, [qid]: val });

  const onSubmit = async () => {
    if (!consent) {
      consentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.error("Você precisa aceitar o termo LGPD para enviar.");
      return;
    }
    setSubmitting(true);
    try {
      const duration_seconds = Math.round((Date.now() - startedAt.current) / 1000);
      await api.post(`/psy/public/${token}/respond`, {
        answers: Object.entries(answers).map(([question_id, value]) => ({ question_id, value })),
        setor: setorFuncao.setor,
        funcao: setorFuncao.funcao,
        duration_seconds,
        consent_lgpd: true,
      });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar");
    } finally {
      setSubmitting(false);
    }
  };

  const totalSteps = groupedByDim.length + 1; // dimensions + final review/consent
  const isIntro = step === 0;
  const isFinal = step === totalSteps;
  const currentDimIndex = step - 1;
  const currentDim = !isIntro && !isFinal ? groupedByDim[currentDimIndex] : null;

  const LIKERT = meta.likert_options || [
    { value: 1, label: "Nunca / Discordo totalmente" },
    { value: 2, label: "Raramente / Discordo" },
    { value: 3, label: "Às vezes / Neutro" },
    { value: 4, label: "Frequentemente / Concordo" },
    { value: 5, label: "Sempre / Concordo totalmente" },
    { value: 0, label: "Não se aplica" },
  ];

  // Check if all questions in current dim are answered (to enable Next)
  const currentDimAnswered = currentDim
    ? currentDim.questions.every((q) => answers[q.id] !== undefined)
    : true;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="psy-public-form">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src={LOGO_URL} alt="Sollute" className="h-9" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold truncate">{meta.test.name}</div>
            <div className="text-sm font-bold text-slate-900 truncate">{meta.titulo}</div>
          </div>
          {!isIntro && (
            <div className="text-right shrink-0">
              <div className="text-xs text-slate-500">{answeredCount}/{totalQuestions}</div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden w-24 mt-1">
                <div className="h-full bg-emerald-500 transition-all duration-300" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Step 0 — Intro */}
        {isIntro && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 mb-4">
              <i className="fa-solid fa-brain text-2xl"></i>
            </span>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 leading-tight">
              {meta.test.name}
            </h1>
            <p className="text-slate-600 mt-2">{meta.test.description}</p>
            <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-slate-700 space-y-2">
              <p><i className="fa-solid fa-shield-halved text-emerald-700 mr-2"></i><b>Anônimo:</b> nenhuma resposta individual será identificada.</p>
              <p><i className="fa-solid fa-clock text-emerald-700 mr-2"></i><b>Sinceridade vale mais que perfeição:</b> não há respostas certas ou erradas.</p>
              <p><i className="fa-solid fa-mobile-screen text-emerald-700 mr-2"></i>O questionário foi otimizado para celular e computador.</p>
            </div>
            {meta.coletar_setor_funcao && (
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Setor (opcional)</label>
                  <input
                    type="text"
                    value={setorFuncao.setor}
                    onChange={(e) => setSetorFuncao({ ...setorFuncao, setor: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
                    data-testid="input-setor"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">Função (opcional)</label>
                  <input
                    type="text"
                    value={setorFuncao.funcao}
                    onChange={(e) => setSetorFuncao({ ...setorFuncao, funcao: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5"
                    data-testid="input-funcao"
                  />
                </div>
              </div>
            )}
            <button
              onClick={() => setStep(1)}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-700 text-white font-semibold py-3 hover:shadow-lg"
              data-testid="btn-start"
            >
              <i className="fa-solid fa-play"></i> Iniciar questionário
            </button>
          </div>
        )}

        {/* Steps 1..N — Dimensions */}
        {currentDim && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8" data-testid={`step-dim-${currentDim.dim.id}`}>
            <div className="text-xs uppercase tracking-wider font-bold text-emerald-700 mb-1">
              Etapa {step} de {totalSteps - 1}
            </div>
            <h2 className="text-xl font-bold text-slate-900">{currentDim.dim.name}</h2>
            <p className="text-sm text-slate-500 mt-1">{currentDim.dim.description}</p>

            <ul className="mt-5 space-y-5 divide-y divide-slate-100">
              {currentDim.questions.map((q, idx) => (
                <li key={q.id} className={idx === 0 ? "" : "pt-5"} data-testid={`question-${q.id}`}>
                  <p className="text-slate-800 font-medium">
                    <span className="text-emerald-700 mr-1.5">{idx + 1}.</span>{q.text}
                  </p>
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {LIKERT.map((opt) => {
                      const selected = answers[q.id] === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setAnswer(q.id, opt.value)}
                          className={`text-left text-sm rounded-lg border px-3 py-2.5 transition ${
                            selected
                              ? "border-emerald-600 bg-emerald-50 text-emerald-900 font-semibold"
                              : "border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50/50"
                          }`}
                          data-testid={`answer-${q.id}-${opt.value}`}
                        >
                          <span className="font-mono text-xs mr-2 opacity-60">{opt.value}</span>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex justify-between gap-3">
              <button
                onClick={() => setStep(step - 1)}
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-slate-700 hover:bg-slate-50"
                data-testid="btn-prev"
              >
                <i className="fa-solid fa-arrow-left mr-1"></i> Voltar
              </button>
              <button
                onClick={() => { setStep(step + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                disabled={!currentDimAnswered}
                className="flex-1 rounded-lg bg-emerald-600 text-white font-semibold px-4 py-2.5 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="btn-next"
              >
                {currentDimAnswered ? "Próximo" : "Responda todas para continuar"}
                <i className="fa-solid fa-arrow-right ml-1"></i>
              </button>
            </div>
          </div>
        )}

        {/* Final step — review & consent */}
        {isFinal && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
            <h2 className="text-xl font-bold text-slate-900"><i className="fa-solid fa-circle-check mr-2 text-emerald-600"></i>Quase lá!</h2>
            <p className="text-sm text-slate-600 mt-1">Você respondeu {answeredCount} de {totalQuestions} questões.</p>

            <div ref={consentRef} className={`mt-5 rounded-xl border p-4 ${consent ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300 ring-2 ring-amber-200"}`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-5 w-5"
                  data-testid="check-consent-lgpd"
                />
                <span className="text-sm text-slate-700">
                  <b>{consent ? "Termo aceito" : "OBRIGATÓRIO"}:</b> Concordo com a coleta anônima das minhas respostas para fins de diagnóstico
                  psicossocial coletivo (conforme NR-01 e LGPD). Minhas respostas individuais não serão exibidas no relatório.
                </span>
              </label>
            </div>

            <div className="mt-6 flex justify-between gap-3">
              <button
                onClick={() => setStep(step - 1)}
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-slate-700 hover:bg-slate-50"
              >
                <i className="fa-solid fa-arrow-left mr-1"></i> Voltar
              </button>
              <button
                onClick={onSubmit}
                disabled={submitting}
                className="flex-1 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-700 text-white font-semibold px-4 py-2.5 hover:shadow-lg disabled:opacity-60"
                data-testid="btn-submit"
              >
                {submitting ? <><i className="fa-solid fa-spinner fa-spin mr-1"></i>Enviando…</> : <><i className="fa-solid fa-paper-plane mr-1"></i>Enviar respostas</>}
              </button>
            </div>
          </div>
        )}
      </main>

      <footer className="max-w-3xl mx-auto px-4 py-6 text-xs text-slate-400 text-center">
        <i className="fa-solid fa-shield-halved mr-1"></i>
        Suas respostas são confidenciais e protegidas pela LGPD. Sollute Ocupacional · {meta.company?.nome_fantasia || ""}
      </footer>
    </div>
  );
}
